import React, { useEffect, useState } from 'react';
import { api, uploadFile, API_ORIGIN } from '../../api.js';

const STEPS = ['Logo', 'Company Profile', 'Country & Tax', 'Done'];

const boxStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 32, width: 440 };

function ProgressDots({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
            background: i <= step ? 'var(--amber)' : 'var(--surface-2)',
            color: i <= step ? '#1a1f28' : 'var(--text-dim)',
          }}>
            {i + 1}
          </div>
          {i < STEPS.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)', margin: '0 6px' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// First-login setup wizard, shown once per tenant (gated by
// tenants.onboarding_completed — see App.jsx) right after the very first
// person at a new company logs in. Reuses the exact same GET/PATCH
// /api/settings endpoint the real Settings page uses, and the same
// POST /dispatch/upload endpoint every document/logo upload already
// goes through — nothing new on the backend besides the flag itself.
export default function OnboardingWizard({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api('/settings').then(s => setSettings({
      companyName: s.company_name || '',
      logoUrl: s.logo_url || '',
      brandColor: s.brand_color || '#F2A93B',
      country: s.country || 'Canada',
      provinceState: s.province_state || '',
    })).catch(() => setSettings({ companyName: '', logoUrl: '', brandColor: '#F2A93B', country: 'Canada', provinceState: '' }));
  }, []);

  const set = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  async function saveStep(patch, andAdvance = true) {
    setSaving(true);
    setError('');
    try {
      await api('/settings', { method: 'PATCH', body: patch });
      if (andAdvance) setStep(s => s + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoFile(file) {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const uploaded = await uploadFile('/dispatch/upload', file);
      set('logoUrl', uploaded.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function finish() {
    setSaving(true);
    setError('');
    try {
      await api('/settings', { method: 'PATCH', body: { onboardingCompleted: true } });
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={boxStyle}>
        <h1 style={{ marginTop: 0, marginBottom: 4 }}>Welcome to TruckFlow</h1>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 0, marginBottom: 18 }}>
          A few quick steps to set up {settings.companyName || 'your company'}'s account, {user?.name || ''}.
        </p>
        <ProgressDots step={step} />

        {step === 0 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Company Logo</h3>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Shown on invoices and across the app. You can skip this and add it later in Settings.</p>
            {settings.logoUrl && (
              <img src={`${API_ORIGIN}${settings.logoUrl}`} alt="Logo preview" style={{ maxWidth: 160, maxHeight: 80, display: 'block', marginBottom: 12, background: '#fff', padding: 8, borderRadius: 6 }} />
            )}
            <input type="file" accept="image/*" onChange={e => handleLogoFile(e.target.files[0])} disabled={uploading} />
            {uploading && <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>Uploading…</p>}
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" disabled={saving} onClick={() => saveStep({ logoUrl: settings.logoUrl })} style={{ width: '100%' }}>
                {settings.logoUrl ? 'Next' : 'Skip for now'}
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Company Profile</h3>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Company name
              <input value={settings.companyName} onChange={e => set('companyName', e.target.value)} style={{ width: '100%', marginTop: 4 }} required />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Brand color
              <input type="color" value={settings.brandColor} onChange={e => set('brandColor', e.target.value)} style={{ width: 80, padding: 4, marginTop: 4 }} />
            </label>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" disabled={saving || !settings.companyName.trim()}
                      onClick={() => saveStep({ companyName: settings.companyName, brandColor: settings.brandColor })}
                      style={{ width: '100%' }}>
                {saving ? 'Saving…' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 style={{ marginTop: 0 }}>Confirm Country & Region</h3>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              This decides which employment rules and tax fields show up across the app — SIN/TD1/CPP-EI for Canada,
              or SSN/W-4/state withholding for the US. You can fine-tune the rest (GST/EIN/etc.) later in Settings.
            </p>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Country
              <select value={settings.country} onChange={e => set('country', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
                <option>Canada</option>
                <option>United States</option>
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              {settings.country === 'United States' ? 'State' : 'Province'}
              <input value={settings.provinceState} onChange={e => set('provinceState', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
            </label>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" disabled={saving}
                      onClick={() => saveStep({ country: settings.country, provinceState: settings.provinceState })}
                      style={{ width: '100%' }}>
                {saving ? 'Saving…' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 style={{ marginTop: 0 }}>You're all set!</h3>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              {settings.companyName}'s account is ready. You can revisit any of this later from Settings.
            </p>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <button type="button" disabled={saving} onClick={finish} style={{ width: '100%', marginTop: 8 }}>
              {saving ? 'Finishing…' : 'Go to my dashboard'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
